import {
  BULK_APPROVAL_SENTENCE_KEYS,
  MARKER_SENTENCE_KEYS,
  ROUTE_SENTENCE_KEYS,
  UNIT_SET_SENTENCE_KEYS,
} from "./constants";
import type {
  ApprovalTarget,
  HistoryTypeName,
  TypeUpdateChange,
} from "../../types";
import type { BuildHistorySentence, BuildHistorySentenceResult } from "./types";

const typeUpdateSentence = (
  change: TypeUpdateChange,
  name: string,
): BuildHistorySentenceResult => {
  if (change.field === "nameStatus") {
    return {
      key:
        change.nameStatus === "confident"
          ? "typeMarkedReviewed"
          : "typeMarkedSuspicious",
      params: { name },
    };
  }
  return change.lvMatchId === null
    ? { key: "lvMatchRemoved", params: { name } }
    : { key: "lvMatchSet", params: { name, position: change.lvMatchId } };
};

const bulkLvMatchSentence = (
  typeName: string | null,
  lvMatchId: string | null,
  planCount: number,
): BuildHistorySentenceResult => {
  const isOnPlans = planCount > 1;
  const onPlans: Record<string, number> = isOnPlans ? { count: planCount } : {};
  if (lvMatchId === null) {
    if (typeName === null) {
      return {
        key: isOnPlans ? "typesUnlinkedOnPlans" : "typesUnlinked",
        params: onPlans,
      };
    }
    return {
      key: isOnPlans ? "lvMatchRemovedOnPlans" : "lvMatchRemoved",
      params: { name: typeName, ...onPlans },
    };
  }
  if (typeName === null) {
    return {
      key: isOnPlans ? "typesLinkedOnPlans" : "typesLinked",
      params: { position: lvMatchId, ...onPlans },
    };
  }
  return {
    key: isOnPlans ? "lvMatchSetOnPlans" : "lvMatchSet",
    params: { name: typeName, position: lvMatchId, ...onPlans },
  };
};

const approvalSentence = (
  target: ApprovalTarget,
  approved: boolean,
  unknownTypeLabel: string,
): BuildHistorySentenceResult => {
  if (target.kind === "diagramItemType") {
    return {
      key: approved ? "approvedDiagramItem" : "unapprovedDiagramItem",
      params: {},
    };
  }
  return {
    key: approved ? "approved" : "unapproved",
    params: { name: target.name ?? unknownTypeLabel },
  };
};

export const buildHistorySentence: BuildHistorySentence = ({
  summary,
  planCount,
  unknownTypeLabel,
}) => {
  const names = (list: HistoryTypeName[]): string =>
    list.map((name) => name ?? unknownTypeLabel).join(", ");

  switch (summary.action) {
    case "element_marker.create":
    case "route.bulk_create": {
      const keys =
        summary.action === "route.bulk_create"
          ? ROUTE_SENTENCE_KEYS
          : MARKER_SENTENCE_KEYS;
      const { count } = summary;
      return summary.createdTypeNames.length > 0
        ? {
            key: keys.addedWithType,
            params: { count, createdTypes: names(summary.createdTypeNames) },
          }
        : {
            key: keys.added,
            params: { count, types: names(summary.typeNames) },
          };
    }
    case "element_marker.delete":
    case "route.delete": {
      const keys =
        summary.action === "route.delete"
          ? ROUTE_SENTENCE_KEYS
          : MARKER_SENTENCE_KEYS;
      const { count } = summary;
      return summary.removedTypeNames.length > 0
        ? {
            key: keys.removedTypeGone,
            params: { count, removedTypes: names(summary.removedTypeNames) },
          }
        : {
            key: keys.removed,
            params: { count, types: names(summary.typeNames) },
          };
    }
    case "element_marker.move":
    case "route.move": {
      const keys =
        summary.action === "route.move"
          ? ROUTE_SENTENCE_KEYS
          : MARKER_SENTENCE_KEYS;
      const params = {
        count: summary.count,
        from: names(summary.fromTypeNames),
        to: names(summary.toTypeNames),
      };
      if (summary.createdTypeNames.length > 0) {
        return { key: keys.movedTypeCreated, params };
      }
      if (summary.removedTypeNames.length > 0) {
        return {
          key: keys.movedTypeGone,
          params: { ...params, removedTypes: names(summary.removedTypeNames) },
        };
      }
      return { key: keys.moved, params };
    }
    case "element_marker.update":
    case "route.update": {
      const keys =
        summary.action === "route.update"
          ? ROUTE_SENTENCE_KEYS
          : MARKER_SENTENCE_KEYS;
      return {
        key: keys.edited,
        params: { count: summary.count, types: names(summary.typeNames) },
      };
    }
    case "element_type.update":
    case "route_type.update":
    case "diagram_item_type.update": {
      return typeUpdateSentence(
        summary.change,
        summary.typeName ?? unknownTypeLabel,
      );
    }
    case "element_type.delete": {
      return {
        key: "typeDeleted",
        params: { count: summary.markerCount, name: summary.typeName },
      };
    }
    // The sentence does not name the kind, so cable types read the same.
    case "element_type.rename":
    case "route_type.rename": {
      const params = {
        from: summary.fromName,
        to: summary.toName ?? unknownTypeLabel,
      };
      if (planCount <= 1) {
        return { key: summary.merged ? "typeMerged" : "typeRenamed", params };
      }
      return {
        key: summary.merged ? "typeMergedOnPlans" : "typeRenamedOnPlans",
        params: { ...params, count: planCount },
      };
    }
    case "route_type.delete": {
      return {
        key: "cableTypeDeleted",
        params: { count: summary.routeCount, name: summary.typeName },
      };
    }
    case "diagram_item_type.delete": {
      return {
        key: "diagramTypeDeleted",
        params: { count: summary.count, name: summary.typeName },
      };
    }
    case "page.calibrate": {
      const { pageNumber, routeCount } = summary;
      return routeCount > 0
        ? {
            key: "pageCalibratedWithRoutes",
            params: { page: pageNumber, count: routeCount },
          }
        : { key: "pageCalibrated", params: { page: pageNumber } };
    }
    case "page.rotate": {
      return {
        key: "pageRotated",
        params: { page: summary.pageNumber, rotation: summary.rotation },
      };
    }
    case "approval.toggle": {
      return approvalSentence(
        summary.target,
        summary.approved,
        unknownTypeLabel,
      );
    }
    case "approval.bulk_approve": {
      const keys = BULK_APPROVAL_SENTENCE_KEYS[summary.targetKind];
      return {
        key: summary.approved ? keys.approved : keys.unapproved,
        params: { count: summary.count },
      };
    }
    case "item_type_setting.set_unit": {
      return {
        key:
          summary.unit === null
            ? "unitRemoved"
            : UNIT_SET_SENTENCE_KEYS[summary.unit],
        params: { name: summary.typeName },
      };
    }
    case "item_type_setting.set_price": {
      return {
        key: "manualPriceChanged",
        params: {
          count: summary.count,
          name: summary.typeName ?? unknownTypeLabel,
        },
      };
    }
    case "item_type_setting.suggest_prices": {
      return { key: "pricesCalculated", params: { count: summary.count } };
    }
    case "duplicate_names.apply": {
      if (planCount <= 1) {
        return { key: "duplicateNamesUnified", params: {} };
      }
      return {
        key: "duplicateNamesUnifiedOnPlans",
        params: { count: planCount },
      };
    }
    case "lv_match.bulk_set": {
      return bulkLvMatchSentence(
        summary.typeName,
        summary.lvMatchId,
        planCount,
      );
    }
    case "lv_match.rematch": {
      if (planCount <= 1) {
        return { key: "lvRematched", params: {} };
      }
      return { key: "lvRematchedOnPlans", params: { count: planCount } };
    }
    case "cable_tray_verdict.set": {
      const params = { name: summary.typeName ?? unknownTypeLabel };
      if (summary.carriesCable === null) {
        return { key: "trayVerdictCleared", params };
      }
      return {
        key: summary.carriesCable
          ? "trayVerdictCarries"
          : "trayVerdictCarriesNot",
        params,
      };
    }
    case "element_marker_comment.set": {
      return {
        key: summary.change === "created" ? "noteAdded" : "noteEdited",
        params: { excerpt: summary.excerpt },
      };
    }
    case "element_marker_comment.delete": {
      return { key: "noteRemoved", params: { excerpt: summary.excerpt } };
    }
    case "unknown": {
      return { key: "unknown", params: {} };
    }
  }
};
